<template>
  <div>
    <!-- Barra superior: título y orden -->
    <v-row class="mb-4" align="center" justify="space-between" no-gutters>
      <v-col cols="12" md="6">
        <h2 class="mb-0">Resumen por máquina</h2>
      </v-col>

      <v-col cols="12" md="6" class="text-md-right mt-2 mt-md-0">
        <v-select :items="ordenItems" v-model="orden" dense outlined hide-details class="d-inline-block"
          style="max-width: 260px" label="Ordenar por promedio" />
      </v-col>
    </v-row>

    <!-- Cartas por máquina -->
    <v-row dense>
      <v-col v-for="maq in maquinasOrdenadas" :key="maq.idMaquinaIns" cols="12" sm="6" md="6" lg="4">
        <v-card class="pa-4">
          <div class="text-subtitle-1 font-weight-medium mb-2">
            [{{ maq.nombreMaquinaIns }}]
          </div>

          <div class="d-flex flex-wrap align-center mb-3">
            <v-chip class="mr-2 mb-2" label small color="blue lighten-5" text-color="blue darken-2">
              <span class="grey--text text--darken-1 mr-1">Promedio:</span>
              <strong>{{ formatDur(maq.promedioSegundos) }}</strong>
            </v-chip>
            <v-chip class="mr-2 mb-2" label small color="success lighten-2" text-color="success darken-2">
              <span class="black--text text--darken-1 mr-1">Min</span>
              <strong>{{ formatDurCompact(maq.minSegundos) }}</strong>
            </v-chip>
            <v-chip class="mr-2 mb-2" label small color="error lighten-2" text-color="error darken-2">
              <span class="black--text text--darken-1 mr-1">Max</span>
              <strong>{{ formatDur(maq.maxSegundos) }}</strong>
            </v-chip>
            <v-chip class="mr-2 mb-2" label small>
              <span class="grey--text text--darken-1 mr-1">Conteo</span>
              <strong>{{ maq.cambios.length }}</strong>
            </v-chip>
          </div>

          <div class="caption mb-1">
            <span class="grey--text text--darken-1">Más lento:</span>
            {{ formatDur((maqMasLento(maq) && maqMasLento(maq).duracion) || 0) }}
          </div>

          <div class="caption mb-4">
            <span class="grey--text text--darken-1">Más rápido:</span>
            {{ formatDur((maqMasRapido(maq) && maqMasRapido(maq).duracion) || 0) }}
          </div>

          <v-btn color="primary" class="mt-auto" @click="abrirDetalles(maq)">
            Ver detalles
          </v-btn>
        </v-card>
      </v-col>
    </v-row>

    <!-- Diálogo con APEXCHART + tabla -->
    <v-dialog v-model="dialog" max-width="960">
      <v-card>
        <v-card-title class="justify-space-between">
          <div class="text-h6">[{{ maquinaSeleccionada ? maquinaSeleccionada.nombreMaquinaIns : '' }}]</div>
          <v-btn icon @click="dialog = false"><v-icon>mdi-close</v-icon></v-btn>
        </v-card-title>

        <v-card-text>
          <!-- ===== Gráfica (ApexCharts) ===== -->
          <div v-if="chartCategories.length" class="mb-6">
            <apexchart height="360" :options="chartOptions" :series="chartSeries" />
          </div>
          <div v-else class="grey--text text--darken-1 mb-6">Sin datos para graficar</div>

          <!-- ===== Tabla de detalles ===== -->
          <v-data-table :headers="headersDialogo" :items="itemsDialogo" dense class="elevation-0" :items-per-page="10"
            item-key="idBloqueOperacionReal" sort-by="durSeg" must-sort :item-class="rowClass">
            <!-- ÚNICO CAMBIO EN SLOT: usa la etiqueta preparada -->
            <template #item.deA="{ item }">
              <div class="de-a">
                <div class="de-a__block">
                  <div class="de-a__title">De</div>
                  <div class="caption" v-if="item.detalleDe && item.detalleDe.area">
                    <strong>Área:</strong> {{ item.detalleDe.area }}
                  </div>
                  <div class="caption" v-if="item.detalleDe && item.detalleDe.ot">
                    <strong>OT:</strong> {{ item.detalleDe.ot }}
                  </div>
                  <div class="caption" v-if="item.detalleDe && item.detalleDe.articulo">
                    <strong>Operación:</strong> {{ item.detalleDe.articulo }}
                  </div>
                  <div class="caption" v-if="item.detalleDe && item.detalleDe.inicio">
                    <strong>Inicio:</strong> {{ formatFechaHora(item.detalleDe.inicio) }}
                  </div>
                  <div class="caption" v-if="item.detalleDe && item.detalleDe.fin">
                    <strong>Fin:</strong> {{ formatFechaHora(item.detalleDe.fin) }}
                  </div>
                </div>

                <div class="de-a__arrow">→</div>

                <div class="de-a__block">
                  <div class="de-a__title">A</div>
                  <div class="caption" v-if="item.detalleA && item.detalleA.area">
                    <strong>Área:</strong> {{ item.detalleA.area }}
                  </div>
                  <div class="caption" v-if="item.detalleA && item.detalleA.ot">
                    <strong>OT:</strong> {{ item.detalleA.ot }}
                  </div>
                  <div class="caption" v-if="item.detalleA && item.detalleA.articulo">
                    <strong>Operación:</strong> {{ item.detalleA.articulo }}
                  </div>
                  <div class="caption" v-if="item.detalleA && item.detalleA.inicio">
                    <strong>Inicio:</strong> {{ formatFechaHora(item.detalleA.inicio) }}
                  </div>
                  <div class="caption" v-if="item.detalleA && item.detalleA.fin">
                    <strong>Fin:</strong> {{ formatFechaHora(item.detalleA.fin) }}
                  </div>
                </div>
              </div>
            </template>

            <template #item.durSeg="{ item }">
              {{ formatDur(item.durSeg) }}
            </template>
          </v-data-table>

          <div class="mt-4 caption">
            <div class="mb-1">
              <span class="grey--text text--darken-1">Más lento:</span>
              {{ formatDur((maqMasLento(maquinaSeleccionada || {}) && maqMasLento(maquinaSeleccionada ||
                {}).duracion) || 0) }}
            </div>
            <div>
              <span class="grey--text text--darken-1">Más rápido:</span>
              {{ formatDur((maqMasRapido(maquinaSeleccionada || {}) && maqMasRapido(maquinaSeleccionada
                || {}).duracion) || 0) }}
            </div>
          </div>
        </v-card-text>

        <v-card-actions>
          <v-spacer />
          <v-btn color="primary" text @click="dialog = false">Cerrar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import VueApexCharts from 'vue-apexcharts'

export default {
  name: 'CambioMoldesCard',
  components: { apexchart: VueApexCharts },

  props: {
    // Puede ser Array de máquinas o { cambiosPorMaquina: [...] }
    data: { type: [Array, Object], required: true },
    peorPrimeroDefault: { type: Boolean, default: true }
  },

  data() {
    return {
      dialog: false,
      maquinaSeleccionada: null,
      resaltarCambioId: null,
      orden: this.peorPrimeroDefault ? 'peor' : 'mejor',
      ordenItems: [
        { text: 'Peor → Mejor (promedio alto primero)', value: 'peor' },
        { text: 'Mejor → Peor (promedio bajo primero)', value: 'mejor' }
      ],

      // ApexChart
      chartOptions: {
        chart: { type: 'line', toolbar: { show: true } },
        stroke: { width: [0, 0, 3], curve: 'smooth' },
        plotOptions: { bar: { columnWidth: '40%' } },
        dataLabels: {
          enabled: true,
          enabledOnSeries: [2], // solo la serie "Promedio"
          formatter: function (v) {
            var s = Math.max(0, Math.floor(v || 0));
            var d = Math.floor(s / 86400);
            var h = Math.floor((s % 86400) / 3600);
            var m = Math.floor((s % 3600) / 60);
            var ss = s % 60;
            var out = [];
            if (d) out.push(d + 'd');
            if (h) out.push(h + 'h');
            if (m) out.push(m + 'm');
            if (!out.length || ss) out.push(ss + 's');
            return out.join(' ');
          }
        },
        xaxis: { categories: [] },
        yaxis: {
          labels: {
            formatter: (v) => {
              const s = Math.max(0, Math.floor(v || 0))
              if (s < 60) return s + 's'
              if (s < 3600) return Math.floor(s / 60) + 'm'
              if (s < 86400) return Math.floor(s / 3600) + 'h'
              return Math.floor(s / 86400) + 'd'
            }
          },
          title: { text: 'Duración' }
        },
        tooltip: {
          y: {
            formatter: (v) => {
              const s = Math.max(0, Math.floor(v || 0))
              const d = Math.floor(s / 86400)
              const h = Math.floor((s % 86400) / 3600)
              const m = Math.floor((s % 3600) / 60)
              const ss = s % 60
              const parts = []
              if (d) parts.push(d + 'd')
              if (h) parts.push(h + 'h')
              if (m) parts.push(m + 'm')
              if (!parts.length || ss) parts.push(ss + 's')
              return parts.join(' ')
            }
          }
        },
        legend: { position: 'top' }
      },
      chartSeries: [],
      chartCategories: []
    }
  },

  computed: {
    cambiosPorMaquina() {
      if (Array.isArray(this.data)) return this.data
      if (this.data && Array.isArray(this.data.cambiosPorMaquina)) return this.data.cambiosPorMaquina
      return []
    },

    maquinasOrdenadas() {
      const copia = this.cambiosPorMaquina.slice()
      if (this.orden === 'peor') {
        copia.sort((a, b) => (b.promedioSegundos || 0) - (a.promedioSegundos || 0))
      } else {
        copia.sort((a, b) => (a.promedioSegundos || 0) - (b.promedioSegundos || 0))
      }
      return copia
    },
    headersDialogo() {
      return [
        { text: 'De → A', value: 'deA', sortable: false },
        { text: 'Duración', value: 'durSeg' } // campo numérico (segundos) para ordenar
      ]
    },
    itemsDialogo() {
      const maq = this.maquinaSeleccionada
      if (!maq || !Array.isArray(maq.cambios)) return []
      return maq.cambios.map(c => ({
        idBloqueOperacionReal: c.idBloqueOperacionReal,
        durSeg: Number(c.duracion || 0),
        detalleDe: c.detalleDe || this.fallbackDetalle(c.etqDe, c.inicio, c.fin),
        detalleA: c.detalleA || this.fallbackDetalle(c.etqA, c.inicio, c.fin)
      }))
    },
  },

  watch: {
    dialog(v) { if (v) this.rebuildChart() },
    maquinaSeleccionada() { this.rebuildChart() }
  },

  methods: {
    rowClass(item) {
      return (item && item.idBloqueOperacionReal === this.resaltarCambioId)
        ? 'primary--text font-weight-medium'
        : ''
    },
    abrirDetalles(maquina, resaltarId) {
      this.maquinaSeleccionada = maquina
      this.resaltarCambioId = resaltarId || null
      this.dialog = true

      this.$nextTick(() => {
        if (!this.resaltarCambioId) return
        const filas = this.$refs.filas
        if (!filas || !Array.isArray(filas)) return
        const fila = filas.find(tr => tr.classList && tr.classList.contains('font-weight-medium'))
        if (fila && fila.scrollIntoView) fila.scrollIntoView({ behavior: 'smooth', block: 'center' })
      })
    },

    // === Construcción de la serie por día (min, max, promedio) ===
    rebuildChart() {
      const maq = this.maquinaSeleccionada
      if (!maq || !Array.isArray(maq.cambios) || !maq.cambios.length) {
        this.chartCategories = []
        this.chartSeries = []
        this.chartOptions = Object.assign({}, this.chartOptions, { xaxis: { categories: [] } })
        return
      }

      const byDay = {} // { 'YYYY-MM-DD': { min, max, sum, n } }
      for (let i = 0; i < maq.cambios.length; i++) {
        const c = maq.cambios[i]
        const d = new Date(c.inicio)
        if (isNaN(d)) continue
        const key = d.getFullYear() + '-' + ('0' + (d.getMonth() + 1)).slice(-2) + '-' + ('0' + d.getDate()).slice(-2)
        if (!byDay[key]) byDay[key] = { min: Infinity, max: 0, sum: 0, n: 0 }
        const s = Number(c.duracion || 0)
        byDay[key].min = Math.min(byDay[key].min, s)
        byDay[key].max = Math.max(byDay[key].max, s)
        byDay[key].sum += s
        byDay[key].n += 1
      }

      const cats = Object.keys(byDay).sort()
      const mins = []
      const maxs = []
      const avgs = []
      for (let i = 0; i < cats.length; i++) {
        const k = cats[i]
        const g = byDay[k]
        mins.push(isFinite(g.min) ? g.min : 0)
        maxs.push(g.max || 0)
        avgs.push(g.n ? Math.round(g.sum / g.n) : 0)
      }

      this.chartCategories = cats
      this.chartSeries = [
        { name: 'Min', type: 'column', data: mins },
        { name: 'Max', type: 'column', data: maxs },
        { name: 'Promedio', type: 'line', data: avgs }
      ]
      this.chartOptions = Object.assign({}, this.chartOptions, { xaxis: { categories: cats } })
    },

    fallbackDetalle(label, inicio, fin) {
      return {
        area: null,
        ot: null,
        articulo: label || null,
        inicio: inicio || null,
        fin: fin || null
      }
    },

    maqMasLento(maq) {
      if (!maq || !Array.isArray(maq.cambios) || !maq.cambios.length) return null
      return maq.cambios.reduce((a, b) => (a.duracion >= b.duracion ? a : b))
    },

    maqMasRapido(maq) {
      if (!maq || !Array.isArray(maq.cambios) || !maq.cambios.length) return null
      return maq.cambios.reduce((a, b) => (a.duracion <= b.duracion ? a : b))
    },

    formatFechaHora(iso) {
      if (!iso) return ''
      const d = new Date(iso)
      return d.toLocaleString('es-MX', {
        year: '2-digit',
        month: '2-digit',
        day: '2-digit',
        hour12: false,
        hour: '2-digit',
        minute: '2-digit'
      })
    },

    formatDur(seg) {
      const s = Math.max(0, Math.floor(Number(seg) || 0))
      const d = Math.floor(s / 86400)
      const h = Math.floor((s % 86400) / 3600)
      const m = Math.floor((s % 3600) / 60)
      const ss = s % 60
      const parts = []
      if (d) parts.push(d + 'd')
      if (h) parts.push(h + 'h')
      if (m) parts.push(m + 'm')
      if (!parts.length || ss) parts.push(ss + 's')
      return parts.join(' ')
    },

    formatDurCompact(seg) {
      const s = Number(seg || 0)
      if (s < 60) return s + 's'
      if (s < 3600) {
        const m = Math.floor(s / 60)
        const ss = s % 60
        return ss ? (m + 'm ' + ss + 's') : (m + 'm')
      }
      return this.formatDur(s)
    }
  }
}
</script>

<style scoped>
.v-card {
  min-height: 220px;
}

.caption a {
  text-decoration: underline;
}

.de-a {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  flex-wrap: wrap;
}

.de-a__block {
  min-width: 220px;
  padding: 6px 8px;
  border-radius: 8px;
  background: #f7f8fb;
}

.de-a__title {
  font-weight: 600;
  margin-bottom: 2px;
}

.de-a__arrow {
  font-weight: 600;
  color: #5f6b7a;
  padding-top: 6px;
}
</style>
